import { useEffect, useRef } from 'react'

/**
 * Scroll-triggered reveal animation hook.
 *
 * Adds `.revealed` class to the element when it enters the viewport.
 * Respects `prefers-reduced-motion` by immediately revealing.
 * Triggers once per element — does not re-hide on scroll out.
 */
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  threshold = 0.1,
) {
  const ref = useRef<T>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    // Respect reduced motion preference
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    if (prefersReducedMotion) {
      el.classList.add('revealed')
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('revealed')
          observer.unobserve(el)
        }
      },
      { threshold },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [threshold])

  return ref
}

/**
 * Counter animation hook.
 *
 * Counts from 0 to `target` over `duration` ms using requestAnimationFrame
 * with ease-out cubic easing. Triggers once when scrolled into view.
 * Respects `prefers-reduced-motion` by showing final value immediately.
 */
export function useCountUp(target: number, duration = 1500, delay = 0) {
  const ref = useRef<HTMLSpanElement>(null)
  const hasAnimated = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches

    if (prefersReducedMotion) {
      el.textContent = String(target)
      return
    }

    el.textContent = '0'

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true
          observer.unobserve(el)

          setTimeout(() => {
            const start = performance.now()
            const animate = (now: number) => {
              const t = Math.min((now - start) / duration, 1)
              // Ease-out cubic
              const eased = 1 - Math.pow(1 - t, 3)
              el.textContent = String(Math.round(eased * target))
              if (t < 1) requestAnimationFrame(animate)
            }
            requestAnimationFrame(animate)
          }, delay)
        }
      },
      { threshold: 0.1 },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [target, duration, delay])

  return ref
}
